import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import {
  useAlertsStore,
  selectUnreadCount,
  ALERT_SEVERITY,
  ALERT_CATEGORY,
  type Alert,
  type AlertSeverity,
} from '../../stores/alertsStore';
import apiClient from '../../api/client';
import { useIsMobile } from '../../hooks/useIsMobile';

const SEVERITY_CONFIG: Record<AlertSeverity, { color: string; bg: string; icon: string }> = {
  [ALERT_SEVERITY.critical]: { color: 'var(--color-negative)', bg: '#fef2f2', icon: 'bi-exclamation-triangle-fill' },
  [ALERT_SEVERITY.warning]: { color: '#d97706', bg: '#fffbeb', icon: 'bi-exclamation-circle-fill' },
  [ALERT_SEVERITY.info]: { color: '#2563eb', bg: '#eff6ff', icon: 'bi-info-circle-fill' },
};

/**
 * Labels for the categories the bell actually raises. Categories with a
 * dashboard card are not built into alerts at all any more (see the note on
 * `buildAlerts` in `hooks/useAlertPolling.ts`), so they are not listed here.
 */
const CATEGORY_LABELS: Record<string, string> = {
  ehrMessage: 'EHR Interface',
  pdpmGap: 'Nutrition',
  bedsideHelp: 'Help Request',
  notOrdered: 'Not Ordered',
};

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function AlertItem({
  alert,
  onNavigate,
}: {
  alert: Alert;
  onNavigate: (alert: Alert) => void;
}) {
  const cfg = SEVERITY_CONFIG[alert.severity];
  const markRead = useAlertsStore((s) => s.markRead);
  const dismissAlert = useAlertsStore((s) => s.dismissAlert);

  return (
    <div
      onClick={() => {
        markRead(alert.id);
        if (alert.link) onNavigate(alert);
      }}
      style={{
        display: 'flex',
        gap: 10,
        padding: '10px 16px',
        cursor: alert.link ? 'pointer' : 'default',
        background: alert.read ? 'transparent' : cfg.bg,
        borderLeft: `3px solid ${cfg.color}`,
        transition: 'background 0.15s',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        if (!alert.read) e.currentTarget.style.background = cfg.bg;
        else e.currentTarget.style.background = 'rgba(0,0,0,0.02)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = alert.read ? 'transparent' : cfg.bg;
      }}
    >
      <i className={`bi ${cfg.icon}`} style={{ color: cfg.color, fontSize: 'var(--font-size-base)', marginTop: 2, flexShrink: 0 }} aria-hidden="true" />
      <span className="sr-only">{alert.severity} alert:</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span
            style={{
              fontSize: 'var(--font-size-small)',
              fontWeight: 600,
              color: cfg.color,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}
          >
            {alert.title}
          </span>
          <span style={{ fontSize: 10, color: 'var(--text-secondary)', marginLeft: 'auto', flexShrink: 0 }}>
            {formatTimestamp(alert.timestamp)}
          </span>
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-small)',
            color: 'var(--text-primary)',
            fontWeight: alert.read ? 400 : 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {alert.message}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>
          {CATEGORY_LABELS[alert.category] || alert.category}
        </div>
      </div>
      <button
        aria-label={`Dismiss ${alert.title} alert`}
        onClick={(e) => {
          e.stopPropagation();
          // For bedside help requests, resolve on the server so it doesn't come back
          if (alert.category === ALERT_CATEGORY.bedsideHelp) {
            const requestId = alert.sourceKey.replace('bedside-help-', '');
            apiClient.post(`/api/Bedside/Help/Update/${requestId}`, { status: 2 }).catch(() => {});
          }
          dismissAlert(alert.id, true);
        }}
        style={{
          background: 'none',
          border: 'none',
          padding: 2,
          cursor: 'pointer',
          flexShrink: 0,
          marginTop: 2,
          opacity: 0.5,
          transition: 'opacity 0.15s',
          display: 'flex',
          alignItems: 'center',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}
      >
        <i className="bi bi-x" style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)' }} aria-hidden="true" />
      </button>
    </div>
  );
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const alerts = useAlertsStore((s) => s.alerts);
  const unreadCount = useAlertsStore(selectUnreadCount);
  const markAllRead = useAlertsStore((s) => s.markAllRead);
  const clearAll = useAlertsStore((s) => s.clearAll);
  // Phones: the pop-up spans the screen less an 8px gutter (TopToolbar.css, .tt-popover).
  const { isMobile } = useIsMobile();

  const open = Boolean(anchorEl);

  const handleNavigate = (alert: Alert) => {
    setAnchorEl(null);
    if (alert.link) navigate(alert.link);
  };

  // Group alerts: critical first, then warning, then info
  const sortedAlerts = [...alerts].sort((a, b) => {
    const sevOrder = { critical: 0, warning: 1, info: 2 };
    const sevDiff = sevOrder[a.severity] - sevOrder[b.severity];
    if (sevDiff !== 0) return sevDiff;
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });

  // Show max 50 in the dropdown
  const displayAlerts = sortedAlerts.slice(0, 50);

  const criticalCount = alerts.filter((a) => !a.read && a.severity === 'critical').length;

  return (
    <>
      <Tooltip title={unreadCount > 0 ? `${unreadCount} unread alert${unreadCount > 1 ? 's' : ''}` : 'No new alerts'}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={unreadCount > 0 ? `Alerts: ${unreadCount} unread` : 'Alerts: none'}
          aria-haspopup="true"
          aria-expanded={open}
          sx={{
            color: criticalCount > 0 ? 'var(--color-negative)' : 'var(--text-secondary)',
            transition: 'color 0.2s',
            animation: criticalCount > 0 ? 'bellPulse 2s infinite' : 'none',
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: 10,
                height: 18,
                minWidth: 18,
                padding: '0 4px',
              },
            }}
          >
            <i className={`bi ${unreadCount > 0 ? 'bi-bell-fill' : 'bi-bell'}`} style={{ fontSize: 'var(--icon-size)' }} aria-hidden="true" />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Live region for screen readers to announce alert count changes */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {unreadCount > 0 ? `${unreadCount} unread alert${unreadCount > 1 ? 's' : ''}` : ''}
      </div>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={isMobile ? 8 : 16}
        slotProps={{
          paper: {
            className: 'tt-popover',
            sx: {
              width: 400,
              maxHeight: 520,
              mt: 1,
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>Alerts</span>
            {unreadCount > 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#fff',
                  background: 'var(--color-negative)',
                  borderRadius: 10,
                  padding: '1px 7px',
                }}
              >
                {unreadCount}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={markAllRead}
                sx={{ fontSize: 'var(--font-size-small)', textTransform: 'none', minWidth: 'auto', padding: '2px 8px' }}
              >
                Mark all read
              </Button>
            )}
            {alerts.length > 0 && (
              <Button
                size="small"
                color="inherit"
                onClick={clearAll}
                sx={{ fontSize: 'var(--font-size-small)', textTransform: 'none', minWidth: 'auto', padding: '2px 8px', color: 'var(--text-secondary)' }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Alert list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {displayAlerts.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px 16px',
                color: 'var(--text-secondary)',
              }}
            >
              <i className="bi bi-bell-slash" style={{ fontSize: 32, opacity: 0.3, marginBottom: 12 }} />
              <span style={{ fontSize: 'var(--font-size-base)' }}>Nothing needs attention</span>
              <span style={{ fontSize: 'var(--font-size-small)', marginTop: 4, opacity: 0.6, textAlign: 'center' }}>
                Problems that aren't on the dashboard show up here.
                <br />
                Today's activity is on the dashboard.
              </span>
            </div>
          ) : (
            displayAlerts.map((alert) => (
              <AlertItem key={alert.id} alert={alert} onNavigate={handleNavigate} />
            ))
          )}
        </div>

        {/* Footer */}
        {alerts.length > 0 && (
          <div
            style={{
              padding: '8px 16px',
              borderTop: '1px solid var(--border-color)',
              textAlign: 'center',
            }}
          >
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                navigate('/dashboard');
              }}
              sx={{ fontSize: 'var(--font-size-small)', textTransform: 'none' }}
            >
              View Dashboard
            </Button>
          </div>
        )}
      </Popover>

      {/* Pulse animation for critical alerts */}
      <style>{`
        @keyframes bellPulse {
          0%, 100% { transform: scale(1); }
          10% { transform: scale(1.15) rotate(5deg); }
          20% { transform: scale(1.15) rotate(-5deg); }
          30% { transform: scale(1.15) rotate(3deg); }
          40% { transform: scale(1); }
        }
      `}</style>
    </>
  );
}
